// Code-action: "Add column to GROUP BY" — Slice 10.
//
// Triggered by the "column is not in the GROUP BY clause" server diagnostic
// (MSSQL 8120, PG 42803, MySQL ER_WRONG_FIELD_WITH_GROUP / 1055, Oracle
// ORA-00979) and by a resolver-side rule for SQLite (where the engine doesn't
// emit a server diagnostic): if the SELECT mixes aggregates with bare columns,
// flag the bare columns and offer to add them all to GROUP BY in one click.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 10.
//
// Implementation strategy: parse the buffer, find the cursor's enclosing
// statement, run `extractQueryStructure` + `classifySelectItems` to gather
// every `needsGroupBy: true` select-item, then call `applyStructuralEdit`
// with a single comma-joined `add-group-by` op so the registry-side textEdits
// represent one atomic change. Monaco applies them as a single undo unit.

import type { ParseOptions, SqlEngine } from '../../../sql-grammar/model';
import {
  buildParsedDocumentFromSource,
  getDocumentStructure,
} from '../../../sql-grammar/document';
import { applyStructuralEdit } from '../../../sql-resolver/edit/applyStructuralEdit';
import { extractQueryStructure } from '../../../sql-resolver/structure/extractQueryStructure';
import type { ParsedBuffer } from '../../../sql-resolver/types';
import { databaseEngineIdToSqlEngine } from '../../../workspace/visual-query-editor/edit/livePullSchemaSnapshot';
import { classifySelectItems } from '../../../workspace/visual-query-editor/detection/aggregateAwareness';
import type {
  CodeActionContext,
  CodeActionEdit,
  CodeActionRegistryEntry,
} from '../../types';

/** Server-emitted diagnostic codes for "column not in GROUP BY". MSSQL "8120",
 *  PG SQLSTATE "42803", MySQL "ER_WRONG_FIELD_WITH_GROUP" / "1055", Oracle
 *  "ORA-00979" / "979". SQLite emits no specific code — detection there falls
 *  back exclusively to the resolver rule. The set is exported so future
 *  diagnostic-message-aware fixes can reuse the canonical mapping. */
export const GROUP_BY_DIAGNOSTIC_CODES: ReadonlySet<string> = new Set<string>([
  '8120',
  '42803',
  'ER_WRONG_FIELD_WITH_GROUP',
  '1055',
  'ORA-00979',
  '979',
]);

const ADD_COLUMN_TO_GROUP_BY_ENTRY_ID = 'add-column-to-group-by' as const;

export const addColumnToGroupByEntry: CodeActionRegistryEntry = {
  id: ADD_COLUMN_TO_GROUP_BY_ENTRY_ID,
  kind: 'quickfix',
  title: 'Add column(s) to GROUP BY',
  canApply: (ctx) => collectFix(ctx) !== null,
  buildEdits: (ctx) => {
    const fix = collectFix(ctx);
    return fix ? [fix.edit] : [];
  },
};

type FixOutcome = {
  readonly edit: CodeActionEdit;
};

function collectFix(ctx: CodeActionContext): FixOutcome | null {
  const source = ctx.sourceText;
  if (!source || source.trim().length === 0) return null;

  const sqlEngine = databaseEngineIdToSqlEngine(ctx.engineId) ?? 'mssql';
  const buffer = parseBuffer(source, sqlEngine);
  if (!buffer) return null;

  const stmt = buffer.documentStructure.statementAt(ctx.cursor.offset)
    ?? buffer.documentStructure.statements[0]
    ?? null;
  if (!stmt) return null;

  let structure;
  try {
    structure = extractQueryStructure(buffer, stmt);
  } catch {
    return null;
  }
  if (structure.kind !== 'select') return null;

  const classifications = classifySelectItems(structure);
  const needsAdd = classifications.filter((c) => c.needsGroupBy && c.columnText.length > 0);
  if (needsAdd.length === 0) return null;

  const columnText = dedupePreserveOrder(needsAdd.map((c) => c.columnText.trim())).join(', ');
  if (columnText.length === 0) return null;

  let modified;
  try {
    modified = applyStructuralEdit(buffer, stmt, { op: 'add-group-by', columnText });
  } catch {
    return null;
  }
  if (modified.plannedEdits.length === 0) return null;

  return {
    edit: {
      id: ADD_COLUMN_TO_GROUP_BY_ENTRY_ID,
      description: needsAdd.length === 1
        ? `Add ${needsAdd[0].columnText.trim()} to GROUP BY`
        : `Add ${needsAdd.length} columns to GROUP BY`,
      textEdits: modified.plannedEdits.map((e) => ({
        range: e.range,
        newText: e.replacement,
      })),
    },
  };
}

function parseBuffer(source: string, sqlEngine: SqlEngine): ParsedBuffer | null {
  const options: ParseOptions = {
    engine: sqlEngine,
    identifierFolding: defaultFoldingForEngine(sqlEngine),
  };
  try {
    const parsedDocument = buildParsedDocumentFromSource(source, options);
    const documentStructure = getDocumentStructure(parsedDocument, 'code-action-buffer');
    return { parsedDocument, documentStructure };
  } catch {
    return null;
  }
}

function defaultFoldingForEngine(engine: SqlEngine): ParseOptions['identifierFolding'] {
  if (engine === 'postgres') return 'lower';
  if (engine === 'oracle') return 'upper';
  return 'case-insensitive';
}

function dedupePreserveOrder(values: readonly string[]): readonly string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const key = v.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(v);
  }
  return out;
}
